import { zonoApi } from '../api/zonoApi';
import {
  AddGroupMembersRequest,
  AddGroupMembersResponse,
  CreateBeatPlanByCustomerRequest,
  CreateBeatPlanByCustomerResponse,
  FetchCustomerProfileRequest,
  FetchCustomerProfileResponse,
  FieldOpsCustomerListRequest,
  FieldOpsCustomerListResponse,
  GetAllGroupsRequest,
  GetAllGroupsResponse,
  GetGroupMembersRequest,
  GetGroupMembersResponse,
  GetScheduleRequest,
  GetTripSummaryRequest,
  GetTripSummaryResponse,
  NonPeriodicBeatPlanInfoResponse,
  RouteScheduleResponse,
  UpdateBeatPlanRequest,
  UpdateBeatPlanResponse,
} from './field-ops-model';

export const FieldOpsApi = zonoApi.injectEndpoints({
  endpoints: (builder) => ({
    getBeatPlanCustomerList: builder.query<
      FieldOpsCustomerListResponse,
      FieldOpsCustomerListRequest
    >({
      query: (args) => {
        return {
          url: `/hub/orgs/api/beat-plan/team/customers/${args.sellerWorkspaceId}`,
          method: 'GET',
          params: { ...args, isReturnNewSemanticConversations: true },
          cache: 'no-cache',
        };
      },
    }),
    getRouteSchedule: builder.query<
      RouteScheduleResponse[],
      GetScheduleRequest
    >({
      query: (args) => ({
        url: `/hub/orgs/api/beat-plan/team/routes/${args.sellerWorkspaceId}`,
        method: 'GET',
        params: args,
        cache: 'no-cache',
      }),
    }),
    getGroupMembers: builder.query<
      GetGroupMembersResponse,
      GetGroupMembersRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/customer-group/${arg?.groupId}/members`,
          method: 'GET',
          params: {
            ...arg,
            isReturnNewSemanticConversations: true,
          },
          cache: 'no-cache',
        };
      },
    }),
    getTripSummary: builder.query<
      GetTripSummaryResponse[],
      GetTripSummaryRequest
    >({
      query(arg) {
        return {
          url: '/hub/orgs/api/trip/upload-summary',
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    getFieldOpsCustomerProfileDetails: builder.mutation<
      FetchCustomerProfileResponse,
      FetchCustomerProfileRequest
    >({
      query({ sellerWorkspaceId, params, customerId }) {
        const { inviteIds, ...restParams } = params;

        const searchParams = new URLSearchParams();

        searchParams.append('sellerWorkspaceId', sellerWorkspaceId);
        searchParams.append('profileCustomerId', customerId);

        // append all inviteIds correctly
        inviteIds.forEach((id) => {
          searchParams.append('inviteIds', id);
        });

        // append remaining numeric params pageNo, pageSize
        Object.entries(restParams).forEach(([key, value]) => {
          searchParams.append(key, String(value));
        });

        return {
          url: `/hub/reports/api/v1/beat-plan-customer-summary?${searchParams.toString()}`,
          method: 'GET',
        };
      },
    }),
    assignRouteToCustomer: builder.mutation<
      AddGroupMembersResponse,
      AddGroupMembersRequest
    >({
      query(arg) {
        const { groupId, body, ...params } = arg;
        return {
          url: `/hub/orgs/api/customer-group/${groupId}/members`,
          method: 'POST',
          params: params,
          body,
        };
      },
    }),
    updateBeatPlan: builder.mutation<
      UpdateBeatPlanResponse,
      UpdateBeatPlanRequest
    >({
      query(arg) {
        const { beatPlanId, body, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/${beatPlanId}`,
          method: 'PUT',
          params: params,
          body: body,
        };
      },
    }),
    getBeatPlanInfo: builder.query<
      NonPeriodicBeatPlanInfoResponse,
      { sellerWorkspaceId: string }
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `/hub/orgs/api/beat-plan/non-periodic-beat-plan`,
          method: 'GET',
          params: {
            sellerWorkspaceId,
          },
        };
      },
    }),
    getAllGroups: builder.query<GetAllGroupsResponse[], GetAllGroupsRequest>({
      query(arg) {
        const { customerGroupTypes, ...otherParams } = arg;
        const params = customerGroupTypes
          ?.map((item) => `customerGroupTypes[]=${item}`)
          ?.join('&');
        return {
          url: `/hub/orgs/api/customer-group?${params}`,
          method: 'GET',
          params: {
            ...otherParams,
            random: Math.random(),
          },
        };
      },
    }),
    createBeatPlanByCustomer: builder.mutation<
      CreateBeatPlanByCustomerResponse,
      CreateBeatPlanByCustomerRequest
    >({
      query(arg) {
        const { body, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan`,
          method: 'POST',
          params: params,
          body: body,
        };
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useLazyGetBeatPlanCustomerListQuery,
  useLazyGetRouteScheduleQuery,
  useLazyGetGroupMembersQuery,
  useLazyGetTripSummaryQuery,
  useGetFieldOpsCustomerProfileDetailsMutation,
  useAssignRouteToCustomerMutation,
  useLazyGetBeatPlanInfoQuery,
  useUpdateBeatPlanMutation,
  useLazyGetAllGroupsQuery,
  useCreateBeatPlanByCustomerMutation,
} = FieldOpsApi;
